import styled from 'styled-components';

import { useCallback } from 'react';
import { nepal } from '@paradigm/design-system/src/colors';
import { useShouldWarnHedgeMark } from '@paradigm/features/src/unified-rfqs/ui/rfqs/hooks';
import LoadingSpinner from '@paradigm/design-system/src/components/LoadingSpinner';
import markPriceHedgeWarnSymbol from '#/components/rfq-common/markPriceHedgeWarnSymbol';
import { useBboView } from '@paradigm/features/src/unified-rfqs/ui/bbo-view-context';

interface Props {
  handleMarkPriceClick?: (price: string) => void;
}

export default function MarkPrice({ handleMarkPriceClick }: Props) {
  // Only while hedge netting is off, where the mark is option-only.
  const shouldWarnHedgeMark = useShouldWarnHedgeMark();
  const bbo = useBboView();
  const { markPrice, markPriceIv } = bbo;
  const markWarnSymbol = shouldWarnHedgeMark && markPriceHedgeWarnSymbol;
  const onMarkPriceClick = useCallback(() => {
    if (markPrice.value != null) {
      handleMarkPriceClick?.(markPrice.value);
    }
  }, [handleMarkPriceClick, markPrice.value]);

  if (bbo.isLoading)
    return (
      <Container>
        <Label>Mark </Label>
        <LoadingSpinner />
      </Container>
    );

  if (bbo.data != null) {
    return (
      <Container>
        <RowContainer>
          <Label>Mark</Label>
          <Price data-testid="mark-price" onClick={onMarkPriceClick}>
            {/* The separating space belongs to the warning symbol, so
                with hedge netting on this renders the mark alone. */}
            {markWarnSymbol ? `${markPrice.formatted} ` : markPrice.formatted}
            {markWarnSymbol}
          </Price>
        </RowContainer>
        {markPriceIv.formatted !== '' && (
          <RowContainer>
            <Label>IV</Label>
            <Price css="opacity:0.4" data-testid="mark-price-iv">
              {markPriceIv.formatted}
            </Price>
          </RowContainer>
        )}
      </Container>
    );
  }

  return (
    <Container>
      <Label>Mark </Label>
      <Price
        title={bbo.error ?? 'Error fetching mark price'}
        css="cursor: help;"
      >
        N/A
      </Price>
    </Container>
  );
}

const Container = styled.div.attrs({ className: 'mark-container' })`
  width: max-content;
  text-align: left;
  vertical-align: baseline;
`;

const Label = styled.span`
  color: ${nepal};
  text-align: right;
  font-size: 12px;
  line-height: 13px;
`;

const Price = styled.span`
  color: white;
  cursor: pointer;
  font-weight: bold;
  font-size: 12px;
  line-height: 13px;
  text-align: left;
`;

const RowContainer = styled.div.attrs({ className: 'mark-row-container' })`
  display: grid;
  grid-template-columns: 29px auto;
  gap: 3px;
`;
